import { useEffect, useState, useCallback } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Pencil, Trash2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => {
      const map = {};
      r.data.forEach((p) => { map[p.id] = p.name; });
      setProducts(map);
    });
  }, [load]);

  const openEdit = (o) => { setEditing(o); setItems(o.items.map((x) => ({ ...x }))); };
  const updQty = (i, q) => setItems(items.map((x, idx) => idx === i ? { ...x, quantity: Math.max(1, parseInt(q) || 1) } : x));
  const rm = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    if (items.length === 0) { toast.error("Order must have at least one item"); return; }
    try {
      await api.put(`/admin/orders/${editing.id}`, { items });
      toast.success("Order updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const repush = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/repush-zoho`);
      if (data.salesorder?.status === "failed") toast.error("Zoho push failed again. Check Zoho credentials / customer.");
      else toast.success(`Pushed to Zoho — ${data.salesorder?.zoho_salesorder_number || "synced"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (id, action) => {
    if (action === "reject" && !window.confirm("Reject this order?")) return;
    try { await api.post(`/admin/orders/${id}/${action}`); toast.success(action === "confirm" ? "Confirmed — sales order pushed to Zoho Books" : "Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pillCls = (s) => s === "confirmed" ? "status-paid" : s === "rejected" ? "status-failed" : "status-unpaid";

  return (
    <div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-6" data-testid="admin-orders-title">Orders</h1>
      <div className="border border-gray-200">
        <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
          <div className="col-span-2">Order ID</div><div className="col-span-3">Customer</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1 text-center">Items</div><div className="col-span-1">Status</div>
          <div className="col-span-3 text-right">Action</div>
        </div>
        {orders.map((o) => (
          <div key={o.id} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 items-center text-sm" data-testid={`admin-order-${o.id}`}>
            <div className="col-span-2 font-mono text-xs truncate">{o.id}
              {o.po_number && <div className="font-sans text-[11px] text-gray-500 normal-case" data-testid={`order-po-${o.id}`}>PO: {o.po_number}</div>}
            </div>
            <div className="col-span-3 truncate">
              <div className="font-medium text-gray-900 truncate">{o.user_name || "—"}</div>
              <div className="text-xs text-gray-500 truncate">{o.user_email}</div>
            </div>
            <div className="col-span-2 text-xs text-gray-600" data-testid={`order-date-${o.id}`}>{new Date(o.created_at).toLocaleString()}</div>
            <div className="col-span-1 text-center">{o.items.length}</div>
            <div className="col-span-1"><span className={`status-pill ${pillCls(o.status)}`}>{o.status.replace("_"," ")}</span></div>
            <div className="col-span-3 text-right">
              {o.status === "pending_approval" && (
                <div className="inline-flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(o)} className="rounded-sm" data-testid={`order-edit-${o.id}`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>
                  <Button size="sm" onClick={() => act(o.id, "confirm")} className="bg-[#0e5a32] hover:bg-[#0a4626] rounded-sm" data-testid={`order-confirm-${o.id}`}><Check className="w-3 h-3 mr-1"/>Confirm</Button>
                  <Button size="sm" variant="outline" onClick={() => act(o.id, "reject")} className="rounded-sm border-red-300 text-red-600 hover:bg-red-50" data-testid={`order-reject-${o.id}`}><X className="w-3 h-3 mr-1"/>Reject</Button>
                </div>
              )}
              {o.status === "confirmed" && (
                <div className="flex flex-col items-end gap-1">
                  {o.salesorder?.status === "failed" ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs text-red-600" data-testid={`zoho-badge-${o.id}`}><AlertTriangle className="w-3.5 h-3.5"/>Zoho failed</span>
                      <button onClick={() => repush(o.id)} className="inline-flex items-center gap-1 text-xs text-[#002fa7] hover:underline" data-testid={`zoho-repush-${o.id}`}><RefreshCw className="w-3 h-3"/>Re-push</button>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-[#0e5a32]" data-testid={`zoho-badge-${o.id}`}><CheckCircle2 className="w-3.5 h-3.5"/>Zoho {o.salesorder?.zoho_salesorder_number || "synced"}</span>
                  )}
                  {o.invoice_id && <a href={`/invoices/${o.invoice_id}`} className="text-xs text-[#002fa7] hover:underline">View invoice →</a>}
                </div>
              )}
            </div>
          </div>
        ))}
        {orders.length === 0 && <div className="p-8 text-sm text-gray-500">No orders yet.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Review order before confirmation</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">Adjust quantity or remove items. Pricing recalculates and a sales order is pushed to Zoho when you confirm.</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {items.map((it, i) => (
              <div key={`${it.product_id}-${it.variant_name}-${i}`} className="grid grid-cols-12 gap-2 items-center border border-gray-200 p-3" data-testid={`edit-item-${i}`}>
                <div className="col-span-7">
                  <div className="text-sm font-medium">{products[it.product_id] || "Unknown product"}</div>
                  <div className="text-xs text-gray-600">{it.variant_name}</div>
                  {it.notes && <div className="text-xs italic text-gray-500 mt-1">"{it.notes}"</div>}
                </div>
                <div className="col-span-3">
                  <Input type="number" min={1} value={it.quantity} onChange={(e) => updQty(i, e.target.value)} className="rounded-sm h-9" data-testid={`edit-qty-${i}`}/>
                </div>
                <div className="col-span-2 text-right">
                  <button onClick={() => rm(i)} className="text-gray-400 hover:text-red-600" data-testid={`edit-remove-${i}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="text-sm text-gray-400 py-4 text-center">No items — add at least one or cancel.</div>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="edit-save">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
